import { useState, useEffect } from "react";
import { FONTS, GRAD, PINK, CREAM, CARD_BORDER, CARD_BG } from "../data/affirmations";
import { collection, query, orderBy, limit, getDocs } from "firebase/firestore";
import { db } from "../firebase";

interface LeaderboardTabProps {
  totalWalks: number;
  totalMiles: number;
  userName: string;
}

interface LeaderboardUser {
  userName: string;
  totalWalks: number;
  totalMiles: number;
  streak: number;
}

function getTimeUntilMonday() {
  const now = new Date();
  const monday = new Date();
  monday.setDate(now.getDate() + ((1 + 7 - now.getDay()) % 7 || 7));
  monday.setHours(0, 0, 0, 0);
  const diff = monday.getTime() - now.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  return { days, hours, mins };
}

export function LeaderboardTab({ totalWalks, totalMiles, userName }: LeaderboardTabProps) {
  const [users, setUsers] = useState<LeaderboardUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [countdown, setCountdown] = useState(getTimeUntilMonday());

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const q = query(collection(db, "users"), orderBy("totalWalks", "desc"), limit(10));
        const snap = await getDocs(q);
        const data = snap.docs.map(doc => doc.data() as LeaderboardUser);
        setUsers(data);
      } catch (e) {
        console.error("Leaderboard fetch error:", e);
      }
      setLoading(false);
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setCountdown(getTimeUntilMonday()), 60000);
    return () => clearInterval(timer);
  }, []);

  const handleInvite = () => {
    const text = "Join me on Hot Girl Walk — London routes, iconic playlists, weekly drops. hotgirlwalks.com";
    if (navigator.share) {
      navigator.share({ title: "Hot Girl Walk", text, url: "https://hotgirlwalks.com" });
    } else {
      navigator.clipboard.writeText(text).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      });
    }
  };

  const getMedal = (i: number) => i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : null;
  const myRank = users.findIndex(u => u.userName === userName) + 1;

  return (
    <div style={{ animation: "fadeUp 0.4s ease" }}>
      <style>{FONTS + `@keyframes fadeUp { from { opacity:0;transform:translateY(16px) } to { opacity:1;transform:translateY(0) } }`}</style>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
        <div>
          <div style={{ fontSize: "9px", letterSpacing: "5px", color: PINK, textTransform: "uppercase", fontWeight: 700, fontFamily: "'Poppins',sans-serif", marginBottom: "4px" }}>this week</div>
          <div style={{ fontFamily: "'Poppins',sans-serif", fontSize: "28px", fontWeight: 900, color: "#1a1a1a", letterSpacing: "-1px", lineHeight: 1 }}>Squad Goals</div>
        </div>
        <div style={{ background: CARD_BG, border: `1px solid ${CARD_BORDER}`, borderRadius: "14px", padding: "10px 14px", textAlign: "center" }}>
          <div style={{ fontSize: "9px", color: PINK, fontWeight: 700, letterSpacing: "1px", fontFamily: "'Poppins',sans-serif", textTransform: "uppercase" }}>Resets in</div>
          <div style={{ fontSize: "15px", fontWeight: 900, color: "#1a1a1a", fontFamily: "'Poppins',sans-serif" }}>
            {countdown.days > 0 ? `${countdown.days}d ${countdown.hours}h` : `${countdown.hours}h ${countdown.mins}m`}
          </div>
        </div>
      </div>

      {/* Your rank */}
      <div style={{ background: CARD_BG, border: `2px solid ${CARD_BORDER}`, borderRadius: "18px", padding: "18px", marginBottom: "12px", display: "flex", alignItems: "center", gap: "16px" }}>
        <div style={{ fontFamily: "'Poppins',sans-serif", fontWeight: 900, fontSize: "36px", background: GRAD, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", minWidth: "50px", textAlign: "center" }}>
          {myRank > 0 ? `#${myRank}` : "—"}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: "9px", color: PINK, letterSpacing: "3px", textTransform: "uppercase", fontWeight: 700, fontFamily: "'Poppins',sans-serif" }}>Your Rank</div>
          <div style={{ fontFamily: "'Poppins',sans-serif", fontWeight: 800, fontSize: "18px", color: "#1a1a1a" }}>{userName || "You"}</div>
          <div style={{ fontSize: "12px", color: "#999", fontFamily: "'Poppins',sans-serif" }}>{totalWalks} walks · {totalMiles} miles</div>
        </div>
        <div style={{ fontSize: "32px" }}>🔥</div>
      </div>

      {/* Leaderboard */}
      <div style={{ background: "#fff", border: `1px solid ${CARD_BORDER}`, borderRadius: "18px", overflow: "hidden", marginBottom: "12px" }}>
        {loading ? (
          <div style={{ padding: "32px", textAlign: "center", fontFamily: "'Poppins',sans-serif", color: "#bbb", fontSize: "14px" }}>
            Loading leaderboard...
          </div>
        ) : users.length === 0 ? (
          <div style={{ padding: "32px", textAlign: "center" }}>
            <div style={{ fontSize: "32px", marginBottom: "8px" }}>👯‍♀️</div>
            <div style={{ fontFamily: "'Poppins',sans-serif", fontWeight: 800, fontSize: "16px", color: "#1a1a1a", marginBottom: "4px" }}>No one here yet</div>
            <div style={{ fontSize: "13px", color: "#999", fontFamily: "'Poppins',sans-serif" }}>Be the first to walk and claim the top spot!</div>
          </div>
        ) : (
          users.map((user, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: "14px", padding: "14px 18px", borderBottom: i < users.length - 1 ? `1px solid ${CARD_BORDER}` : "none", background: user.userName === userName ? CARD_BG : "#fff" }}>
              <div style={{ fontFamily: "'Poppins',sans-serif", fontWeight: 900, fontSize: "20px", minWidth: "28px", textAlign: "center" }}>
                {getMedal(i) || <span style={{ color: "#bbb", fontSize: "14px" }}>#{i + 1}</span>}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: "'Poppins',sans-serif", fontWeight: 700, fontSize: "14px", color: "#1a1a1a" }}>
                  {user.userName} {user.userName === userName ? "👑" : ""}
                </div>
                <div style={{ fontSize: "11px", color: "#bbb", fontFamily: "'Poppins',sans-serif" }}>{user.totalMiles} miles · {user.streak} day streak</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontFamily: "'Poppins',sans-serif", fontWeight: 900, fontSize: "20px", background: GRAD, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>{user.totalWalks}</div>
                <div style={{ fontSize: "9px", color: "#bbb", letterSpacing: "1px", textTransform: "uppercase", fontFamily: "'Poppins',sans-serif" }}>walks</div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Invite */}
      <div style={{ background: CARD_BG, border: `1px solid ${CARD_BORDER}`, borderRadius: "16px", padding: "20px", textAlign: "center" }}>
        <div style={{ fontSize: "28px", marginBottom: "8px" }}>👯‍♀️</div>
        <div style={{ fontFamily: "'Poppins',sans-serif", fontWeight: 800, fontSize: "17px", color: "#1a1a1a", marginBottom: "4px" }}>Invite your girls</div>
        <div style={{ fontSize: "12px", color: "#999", fontFamily: "'Poppins',sans-serif", marginBottom: "14px" }}>See who's walking more — you or them 👀</div>
        <button
          onClick={handleInvite}
          style={{ padding: "12px 28px", borderRadius: "50px", border: "none", cursor: "pointer", background: GRAD, color: "#fff", fontSize: "13px", fontWeight: 700, fontFamily: "'Poppins',sans-serif", boxShadow: "0 4px 16px rgba(255,105,180,0.3)" }}>
          {copied ? "Copied! 💕" : "Invite Girls"}
        </button>
      </div>
    </div>
  );
}
